import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, BellRing, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * KidBellTray — Iteration 132.
 *
 * Renders the alert bell in the Kid Zone top nav. Polls
 * `/api/kid/notifications` every 45s so a "Back in Stock!" alert
 * appears without a full page reload. Clicking the bell opens a
 * tray of alert cards; each card links straight to the listing
 * with a "Buy with Piggy Bank" affordance in the listing itself.
 */
export default function KidBellTray() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const trayRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/kid/notifications");
      setRows(data?.notifications || []);
      setUnread(data?.unread || 0);
    } catch { /* silent — no session or endpoint just failed */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [load]);

  // Close tray on outside-click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (trayRef.current && !trayRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/kid/notifications/mark-all-read");
      setUnread(0);
      setRows((r) => r.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    } catch { /* silent */ }
  }, []);

  return (
    <div className="relative" ref={trayRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white border border-slate-200 hover:border-brandTeal text-slate-700 hover:text-brandTeal transition"
        data-testid="kid-bell-btn"
        aria-label={unread > 0 ? `${unread} unread alerts` : "Alerts"}
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brandPink text-white text-[10px] font-bold inline-flex items-center justify-center border-2 border-white"
            data-testid="kid-bell-unread"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 top-12 z-40 w-80 max-w-[92vw] rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden"
          data-testid="kid-bell-tray"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="font-bold text-slate-900 flex items-center gap-2">
              <BellRing className="w-4 h-4 text-brandPink" data-testid="kid-bell-tray-header-icon" /> Alerts
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-[11px] font-bold text-brandTeal hover:text-brandTealDark uppercase tracking-wider"
                data-testid="kid-bell-mark-all-read"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto" data-testid="kid-bell-list">
            {loading ? (
              <div className="p-6 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="kid-bell-empty">
                No alerts right now.
              </div>
            ) : (
              rows.map((n) => (
                <Link
                  key={n.id}
                  to={`/listing/${n.listing_id}`}
                  className={`block px-4 py-3 border-b border-slate-100 hover:bg-brandTeal/5 transition ${n.read_at ? "opacity-70" : ""}`}
                  onClick={async () => {
                    setOpen(false);
                    if (!n.read_at) {
                      try { await api.post(`/kid/notifications/${n.id}/read`); } catch {}
                    }
                  }}
                  data-testid={`kid-bell-item-${n.id}`}
                >
                  <div className="flex items-center gap-3">
                    {n.listing_thumbnail && (
                      <img src={n.listing_thumbnail} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold uppercase tracking-wider text-brandPink">
                        Back in stock!
                      </div>
                      <div className="text-sm font-semibold text-slate-900 truncate">
                        {n.listing_title || "One of your saved toys"}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        Tap to see it &amp; buy with your Piggy Bank
                      </div>
                    </div>
                    {!n.read_at && (
                      <span className="w-2 h-2 rounded-full bg-brandPink flex-shrink-0" aria-label="unread" />
                    )}
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
